import { filter, Observable } from "rxjs";

import { map, withLatestFrom } from "rxjs/operators";

import { Coordinates } from "@hermes/api-model-common";

import {
    HoveredShop,
    HoveredShopState,
    SelectedLocationCity,
    SelectedLocationCoordinates,
    SelectedLocationCountry,
    SelectedLocationState,
    ShopLocatorExtraInfos,
    ShopsStateData,
} from "../reducers/shop-locator.state";

/**
 * Describe if the selected location is a GPS coordinates (with ou without bounds attribut)
 *
 * Locations with GPS coordinates are locations searched by users (ex: Google search)
 */
export const isCoordinates = (
    selectedLocationState: SelectedLocationState,
): selectedLocationState is SelectedLocationCoordinates =>
    (selectedLocationState as SelectedLocationCoordinates).coordinates !==
    undefined;

/**
 * Describe if the selected location is a GPS coordinates (with ou without bounds attribut) AND is not a country
 *
 * For exemple, with a Google search, we can know if the selected location is a country or not.
 */
export const isCoordinatesAndNoCountry = (
    selectedLocationState: SelectedLocationState,
): selectedLocationState is SelectedLocationCoordinates & {
    isCountry: false;
} => isCoordinates(selectedLocationState) && !selectedLocationState.isCountry;

/**
 * Describe if the selected location is a country.
 *
 * This location is not trigger by a user search.
 */
export const isCountry = (
    selectedLocationState: SelectedLocationState,
): selectedLocationState is SelectedLocationCountry =>
    (selectedLocationState as SelectedLocationCountry).countryCode !==
        undefined &&
    (selectedLocationState as SelectedLocationCity).city === undefined;

/**
 * Describe if the selected location is a city. A search by city must have a country code.
 *
 * This location is not trigger by a user search.
 */
export const isCity = (
    selectedLocationState: SelectedLocationState,
): selectedLocationState is SelectedLocationCity =>
    (selectedLocationState as SelectedLocationCountry).countryCode !==
        undefined &&
    (selectedLocationState as SelectedLocationCity).city !== undefined;

export const isDefinedHoveredShop = (
    hoveredShop: HoveredShopState,
): hoveredShop is HoveredShop => hoveredShop !== "NO_HOVERED_SHOP";

export const isDefinedSelectedLocation = (
    selectedLocationState: SelectedLocationState,
): selectedLocationState is
    | SelectedLocationCoordinates
    | SelectedLocationCity
    | SelectedLocationCountry =>
    selectedLocationState !== "NO_SELECTED_LOCATION";

/**
 * Calculation for a given countryCode, to the shops to be displayed (with or without map).
 *
 * If there is no shop in the country, we return all shops.
 */
export const calculateVisibleShopsByCountry = (
    shops: ShopsStateData,
    countryCode: string,
): ShopsStateData => {
    const visibleShops: ShopsStateData = {};
    for (const shopId in shops) {
        if (shops[shopId].countryCode === countryCode) {
            visibleShops[shopId] = shops[shopId];
        }
    }

    if (Object.keys(visibleShops).length > 0) {
        return visibleShops;
    }

    return shops;
};

/**
 * Calculation for a given city and countryCode, the shops to be displayed (with or without map).
 *
 * If there is no shop, we return an empty object.
 */
export const calculateVisibleShopsByCountryAndCity = (
    shops: ShopsStateData,
    countryCode: string,
    translatedCity: string,
): ShopsStateData => {
    const visibleShops: ShopsStateData = {};
    for (const shopId in shops) {
        if (
            shops[shopId].city === translatedCity &&
            shops[shopId].countryCode === countryCode
        ) {
            visibleShops[shopId] = shops[shopId];
        }
    }
    return visibleShops;
};

/**
 * Sort the list of shop by putting the shop that match the countryCode in parameter first.
 */
export const sortShopIdsByCountry = (
    shopIds: string[],
    shops: ShopsStateData,
    countryCode: string,
): string[] => [
    ...shopIds.filter((shopId) => shops[shopId].countryCode === countryCode),
    ...shopIds.filter((shopId) => shops[shopId].countryCode !== countryCode),
];

/**
 * Sort the list of shop by putting the shop that match the translatedCity in parameter first.
 */
export const sortShopIdsByCity = (
    shopIds: string[],
    shops: ShopsStateData,
    translatedCity: string,
): string[] => [
    ...shopIds.filter((shopId) => shops[shopId].city === translatedCity),
    ...shopIds.filter((shopId) => shops[shopId].city !== translatedCity),
];

/**
 * Sort the list of shop by putting the shop from the closest to the farthest
 */
export const sortShopIdsByMeters = (
    shopIds: string[],
    extraInfos: ShopLocatorExtraInfos,
): string[] => {
    const shopIdsCopy = [...shopIds];
    shopIdsCopy.sort((a, b) => extraInfos[a].meters - extraInfos[b].meters);
    return shopIdsCopy;
};

/**
 * From a Shop list, returns the list of stores included in a bounds.
 */
export const getShopsIdInBounds = (
    bounds: google.maps.LatLngBounds,
    shopIds: string[],
    shops: ShopsStateData,
): string[] =>
    shopIds.filter((shopId) =>
        bounds.contains({
            lat: shops[shopId].geoCoordinates.latitude,
            lng: shops[shopId].geoCoordinates.longitude,
        }),
    );

export const extendBounds = (
    coordinates: Coordinates,
    bounds: google.maps.LatLngBounds,
): void => {
    bounds.extend({
        lat: coordinates.latitude,
        lng: coordinates.longitude,
    });
};

/**
 * Return Observable that emits a new value for each map pin that is hovered (only map - not list).
 *
 * The value emitted is the position in shopLocatorItems list of the hovered shop (0 < index < shopLocatorItems.length - 1).
 */
const getHoveredShopIndex = (
    hoveredShop$: Observable<HoveredShopState>,
    visibleShopIds$: Observable<string[]>,
): Observable<number> =>
    hoveredShop$.pipe(
        filter(isDefinedHoveredShop),
        filter(
            (hoveredShop) =>
                // No need to scroll when the event comes from the list: the user just hovers over it.
                hoveredShop.origin === "map",
        ),
        withLatestFrom(visibleShopIds$),
        map(([hoveredShop, visibleShopIds]) =>
            visibleShopIds.indexOf(hoveredShop.shopId),
        ),
        filter((index) => index !== -1),
    );

export const getHoveredAndVisibleShopsIds = (
    visibleShopIds$: Observable<string[]>,
    hoveredShop$: Observable<HoveredShopState>,
): Observable<[number, string[]]> =>
    getHoveredShopIndex(hoveredShop$, visibleShopIds$).pipe(
        withLatestFrom(visibleShopIds$),
    );
